// CSS source values; percentage strings become fractional numbers at runtime (e.g. logoWidth 0.05).
const styleVarDefinitions = {
	// Example: this results in 0.05 when used in JS, and 5% when used in CSS.
	logoWidth: '5%',
	fontFamilyPrimary: 'Poppins',
	// Soft blue in light mode, and dark blue in dark mode.
	appBackground: '#f6f6f6',
	appBackgroundDark: '#121316',
	/** 3D viewport fill behind the GLB scene (drippy-scene). */
	sceneHostBackground: '#C9C9C9',

	uiSpacing: 20,
	uiSpacingMedium: 15,
	uiSpacingSmall: 8,
	uiSpacingTiny: 5,
	uiSpacingLarge: 30,
	uiSpacingXl: 40,
	uiSpacingXxl: 60,
	uiGap: 10,
	uiGapSmall: 5,
	uiGapLarge: 15,

	// UI Colors
	uiColorPrimaryBlack: '#121316',
	uiColorPrimaryWhite: '#ffffff',
	uiColorPrimaryLightGrey: '#f6f6f6',
	uiColorAccentViolet: '#b897fd',
	uiColorSecondaryDarkGrey: '#2a2c31',
	uiColorSecondaryLightGrey: '#99999a',
	uiColorDarkGrey: '#292b30',
	uiColorLightGrey: '#E0E1E4',
	uiColorBorderColor: '#f6f6f6',
	uiColorFocusColor: '#4a90e2',
	uiColorShadowColor: 'rgba(0, 0, 0, 0.1)',
	uiColorWhiteShadow: '#ffffff40',
	uiColorLightPurple: '#b897fd',

	// Border and Border Radius
	borderWidth: 1,
	borderRadius: 10,
	borderRadiusSmall: 8,
	borderRadiusMedium: 12,
	borderRadiusLarge: 15,
	borderRadiusXl: 20,
	borderRadiusXxl: 50,
	borderRadiusCircular: '50%',
	borderRadiusPill: 100,

	fontFamily: 'Poppins, sans-serif',

	// Font Sizes
	fontSizeTextXl: 54,
	fontSizeTextLg: 40,
	fontSizeTextMd: 16,
	fontSizeTextSm: 14,
	fontSizeTextXs: 12,
	fontSizeTextXxs: 10,
	fontSizeTextXxxs: 9,
	// Responsive sizes
	fontSizeTextXlTablet: 44,
	fontSizeTextLgTablet: 28,
	fontSizeTextMdTablet: 20,
	fontSizeTextSmTablet: 16,
	fontSizeTextXsTablet: 14,
	fontSizeTextXlMobile: 28,
	fontSizeTextLgMobile: 20,
	fontSizeTextMdDesktop: 18,
	fontSizeTextSmDesktop: 21,
	// Font Weights
	fontWeightBold: 700,
	fontWeightSemiBold: 600,
	fontWeightMedium: 500,
	fontWeightNormal: 400,
	// Line Heights
	lineHeightNormal: 'normal',
	lineHeightTight: 1.2,
	lineHeightRelaxed: 1.3,
	lineHeightLoose: 1.5,

	// Transitions
	/* approximate an exponential curve */
	transitionCurveExpo: 'cubic-bezier(0.0, 1.0, 0.0, 1.0)',
	transitionCurveExpoInOut: 'cubic-bezier(0.8, 0.0, 0.2, 1.0)',
	transitionTimeFast: '0.3s',
	transitionTimeSlow: '0.8s',
	transitionTimeDefault: 'var(--transitionTimeSlow)',
	transitionDefaultTimeCurve: 'var(--transitionTimeDefault) var(--transitionCurveExpoInOut)',
	// TODO delete old transition vars below in favor of new ones above
	transitionFast: '0.3s ease',
	transitionSlow: 'all 0.3s ease',
	transitionHover: 'color 0.3s ease',
	transitionTransform: 'transform 0.3s ease',

	// Responsive Breakpoints
	breakpointDesktop: 769,
	breakpointTablet: 768,
	breakpointMobile: 480,

	// Grid spacing
	gridGapMobile: 25,
	gridGapTablet: 25,
	gridGapDesktop: 30,

	// Card heights
	cardHeightMobile: 220,
	cardHeightDesktop: 280,
	cardHeightLarge: 310,

	// Button heights
	buttonHeight: 38,
} as const

type StyleVarDefinition = typeof styleVarDefinitions
type StyleVarRuntimeValue<V> = V extends `${number}%` ? number : V
type StyleVars = {[K in keyof StyleVarDefinition]: StyleVarRuntimeValue<StyleVarDefinition[K]>}

const buildStyleVars = (): StyleVars =>
	Object.fromEntries(
		(Object.keys(styleVarDefinitions) as Array<keyof StyleVarDefinition>).map(key => {
			const val = styleVarDefinitions[key]
			const runtimeVal = typeof val === 'string' && val.endsWith('%') ? Number(val.replace('%', '')) / 100 : val
			return [key, runtimeVal]
		}),
	) as StyleVars

const styleVars = buildStyleVars()

declare global {
	interface Window {
		styleVars: typeof styleVars
	}
}

window.styleVars = styleVars

const SHADOW_FONT_CSS = /* css */ `
	*,
	button,
	[role='button'],
	input,
	select,
	textarea {
		font-family: var(--fontFamily, ${styleVarDefinitions.fontFamily});
	}
	button,
	[role='button'],
	input[type='button'],
	input[type='submit'],
	input[type='reset'] {
		/* UA button rules beat a bare *; keep Poppins even if --fontFamily is unset. */
		font-family: var(--fontFamily, ${styleVarDefinitions.fontFamily}) !important;
	}
`

let shadowFontSheetInstalled = false

function installShadowFontStyles() {
	if (shadowFontSheetInstalled || typeof document === 'undefined') return
	shadowFontSheetInstalled = true

	const sheet = new CSSStyleSheet()
	sheet.replaceSync(SHADOW_FONT_CSS)

	const adopt = (root: Document | ShadowRoot) => {
		const sheets = root.adoptedStyleSheets ?? []
		if (!sheets.includes(sheet)) root.adoptedStyleSheets = [...sheets, sheet]
	}

	adopt(document)

	const style = document.createElement('style')
	style.setAttribute('data-shadow-font-styles', '')
	style.textContent = SHADOW_FONT_CSS
	document.head.append(style)

	const walk = (root: ParentNode) => {
		if (root instanceof Element && root.shadowRoot) adopt(root.shadowRoot)
		for (const el of root.querySelectorAll('*')) if (el.shadowRoot) adopt(el.shadowRoot)
	}

	walk(document.documentElement)

	/*
	 * MutationObserver on document cannot see nodes created inside shadow trees.
	 * Lume components nest deeply (app → panel → card → overlay), so patch
	 * attachShadow to adopt the font sheet into every new shadow root.
	 */
	const originalAttachShadow = Element.prototype.attachShadow
	Element.prototype.attachShadow = function (init: ShadowRootInit): ShadowRoot {
		const shadow = originalAttachShadow.call(this, init)
		adopt(shadow)
		return shadow
	}

	new MutationObserver(mutations => {
		for (const mutation of mutations) {
			for (const node of mutation.addedNodes) {
				if (!(node instanceof Element)) continue
				if (node.shadowRoot) adopt(node.shadowRoot)
				walk(node)
			}
		}
	}).observe(document.documentElement, {childList: true, subtree: true})
}

{
	const css = String.raw // for syntax/formatting

	const style = document.createElement('style')

	// Define which properties should not have 'px' added
	const unitlessProperties = [
		'fontWeightSemiBold',
		'fontWeightBold',
		'fontWeightMedium',
		'fontWeightNormal',
		'lineHeightTight',
		'lineHeightRelaxed',
		'lineHeightLoose',
		'borderRadiusCircular',
	]

	style.textContent = css`
		:root {
			${Object.entries(styleVarDefinitions)
				.map(([k, v]) => {
					const shouldAddPx = typeof v === 'number' && !unitlessProperties.includes(k)
					return `--${k}: ${shouldAddPx ? v + 'px' : v};`
				})
				.join('\n')}
		}
	`

	document.head.append(style)

	installShadowFontStyles()
}
